import { EXPLOSION_FRAMES } from '../spriteLayout';
import { drawSprite } from './drawSprite';
import type { Blast, Scene } from './types';
import type { PlaybackState, SmokeScreenFx } from './playbackState';

// WHY: iOS Safari игнорирует ctx.filter='blur()' — блюр запечён уменьшением картинки в маленький canvas и апскейлом
const blurCache = new WeakMap<HTMLImageElement, HTMLCanvasElement>();
const blurOf = (bg: HTMLImageElement): HTMLCanvasElement | null => {
  const cached = blurCache.get(bg);
  if (cached) return cached;
  if (!bg.complete || !bg.naturalWidth) return null;
  const w = 48;
  const h = Math.max(1, Math.round((w * bg.naturalHeight) / bg.naturalWidth));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d');
  if (!x) return null;
  x.imageSmoothingEnabled = true;
  x.drawImage(bg, 0, 0, w, h);
  blurCache.set(bg, c);
  return c;
};

const drawBackground = (ctx: CanvasRenderingContext2D, bg: HTMLImageElement, cw: number, ch: number) => {
  const src = blurOf(bg);
  if (!src) {
    const s = Math.max(cw / bg.width, ch / bg.height);
    ctx.drawImage(bg, (cw - bg.width * s) / 2, (ch - bg.height * s) / 2, bg.width * s, bg.height * s);
    return;
  }
  const smoothed = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = true;
  const scale = Math.max(cw / src.width, ch / src.height) * 1.06;
  const w = src.width * scale, h = src.height * scale;
  ctx.drawImage(src, (cw - w) / 2, (ch - h) / 2, w, h);
  ctx.imageSmoothingEnabled = smoothed;
  ctx.fillStyle = 'rgba(0,0,0,0.4)';
  ctx.fillRect(0, 0, cw, ch);
};

export const ensureBackdrop = (
  sc: Scene, cw: number, ch: number, dpr: number, fieldX: number, offY: number, scale: number,
): HTMLCanvasElement | null => {
  const key = `${Math.round(cw)}x${Math.round(ch)}x${dpr}|${fieldX.toFixed(2)},${offY.toFixed(2)},${scale.toFixed(4)}|${blurOf(sc.bg) ? 1 : 0}|${sc.bg.src}`;
  if (sc.backdrop && sc.backdrop.key === key) return sc.backdrop.canvas;
  const dw = Math.max(1, Math.round(cw * dpr));
  const dh = Math.max(1, Math.round(ch * dpr));
  const cv = sc.backdrop?.canvas ?? document.createElement('canvas');
  cv.width = dw;
  cv.height = dh;
  const octx = cv.getContext('2d');
  if (!octx) return null;
  octx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawBackground(octx, sc.bg, cw, ch);
  octx.save();
  octx.translate(fieldX, offY);
  octx.scale(scale, scale);
  octx.drawImage(sc.bg, 0, 0, sc.W, sc.H);
  octx.restore();
  sc.backdrop = { canvas: cv, key };
  return cv;
};

export const drawEffects = (sc: Scene, time: number) => {
  const { ctx, bullets, flashes, impacts, grenades, slashes, nade, smokeNade, flashNade, slash } = sc;
  ctx.lineCap = 'round';
  for (const b of bullets) {
    const k = (time - b.t0) / b.life;
    const x = b.fromX + (b.toX - b.fromX) * k;
    const y = b.fromY + (b.toY - b.fromY) * k;
    ctx.strokeStyle = `rgba(255, 224, 118, ${1 - k})`;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(b.fromX, b.fromY);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  for (const fl of flashes) {
    const k = (time - fl.t0) / fl.life;
    ctx.save();
    ctx.translate(fl.x, fl.y);
    ctx.rotate(fl.angle);
    ctx.fillStyle = `rgba(255, 238, 120, ${1 - k})`;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(36, -10);
    ctx.lineTo(58, 0);
    ctx.lineTo(36, 10);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  for (const im of impacts) {
    const k = (time - im.t0) / im.life;
    ctx.strokeStyle = `rgba(255, 245, 180, ${1 - k})`;
    ctx.lineWidth = 2.5;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      ctx.beginPath();
      ctx.moveTo(im.x, im.y);
      ctx.lineTo(im.x + Math.cos(a) * 22 * k, im.y + Math.sin(a) * 22 * k);
      ctx.stroke();
    }
  }
  for (const g of grenades) {
    const k = (time - g.t0) / (g.land - g.t0);
    const fwd = 1 - (1 - k) * (1 - k);
    const x = g.fromX + (g.toX - g.fromX) * fwd;
    const ground = g.fromY + (g.toY - g.fromY) * fwd;
    const BOUNCES = 3;
    const hop = Math.abs(Math.sin(k * Math.PI * BOUNCES)) * 230 * (1 - k) * (1 - k);
    const squash = hop < 6 ? 1.5 : 1;
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.ellipse(x, ground, 14 * squash, 6 / squash, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    const spr = g.flash ? flashNade : g.smoke ? smokeNade : nade;
    const gs = 70 / spr.bh;
    ctx.save();
    ctx.translate(x, ground - hop);
    ctx.rotate(k * 20);
    ctx.scale(gs, gs);
    drawSprite(ctx, spr, -(spr.bx + spr.bw / 2), -(spr.by + spr.bh / 2));
    ctx.restore();
  }
  for (const sl of slashes) {
    const k = (time - sl.t0) / sl.life;
    ctx.save();
    ctx.translate(sl.x, sl.y);
    ctx.rotate(sl.angle - 0.6 + k * 1.2);
    ctx.globalAlpha = 1 - k;
    const ss = 200 / slash.bw;
    ctx.scale(ss, ss);
    drawSprite(ctx, slash, -(slash.bx + slash.bw / 2), -(slash.by + slash.bh / 2));
    ctx.restore();
  }
};

export const drawOneBlast = (sc: Scene, bl: Blast, time: number) => {
  const { ctx, explosionImg, explosionFrames } = sc;
  const frames = explosionFrames.length
    ? explosionFrames
    : Array.from({ length: EXPLOSION_FRAMES }, (_, i) => ({
        sx: (explosionImg.width / EXPLOSION_FRAMES) * i,
        sw: explosionImg.width / EXPLOSION_FRAMES, sy: 0, sh: explosionImg.height,
      }));
  const k = Math.min(0.999, (time - bl.t0) / bl.life);
  const fr = frames[Math.min(frames.length - 1, (k * frames.length) | 0)];
  const size = bl.r * 2.4;
  const h = (size * fr.sh) / fr.sw;
  ctx.drawImage(explosionImg, fr.sx, fr.sy, fr.sw, fr.sh, bl.x - size / 2, bl.y - h / 2, size, h);
};

const CASING_G = 1600;
const CASING_BOUNCE = 0.4;
const CASING_REST_S = 3.6;
const CASING_FADE_S = 1.3;

export const spawnCasing = (sc: Scene, muzzleX: number, muzzleY: number, angle: number, dir: 1 | -1, feetY: number, mine: boolean, time: number) => {
  const r = Math.random;
  const x0 = muzzleX - Math.cos(angle) * 26;
  const y0 = muzzleY - Math.sin(angle) * 26 - 8;
  const vx = dir * (30 + r() * 90) + (r() - 0.5) * 150;
  const vy = -(270 + r() * 240);
  const groundY = feetY + (r() - 0.35) * 46;
  const landT = (-vy + Math.sqrt(vy * vy + 2 * CASING_G * (groundY - y0))) / CASING_G;
  const landX = x0 + vx * landT;
  const bounceVy = -CASING_BOUNCE * (vy + CASING_G * landT);
  const bounceVx = vx * 0.5;
  const landT2 = landT + (-2 * bounceVy) / CASING_G;
  sc.casings.push({
    x0, y0, vx, vy,
    groundY, landT, landX,
    bounceVx, bounceVy, landT2, restX: landX + bounceVx * (landT2 - landT),
    restRot: (r() < 0.5 ? 1 : -1) * (0.06 + r() * 0.16),
    rot0: r() * Math.PI * 2,
    spin: (12 + r() * 12) * (r() < 0.5 ? -1 : 1),
    flip: r() < 0.5 ? 1 : -1,
    t0: time, life: landT2 + CASING_REST_S + CASING_FADE_S,
    landed: false, mine,
  });
};

export const drawOneCasing = (sc: Scene, c: Scene['casings'][number], time: number) => {
  const { ctx, casing } = sc;
  const cs = 20 / casing.bw;
  const t = time - c.t0;
  if (t < 0 || t >= c.life) return;
  let x: number, y: number, rot: number;
  if (t < c.landT) {
    x = c.x0 + c.vx * t;
    y = c.y0 + c.vy * t + 0.5 * CASING_G * t * t;
    rot = c.rot0 + c.spin * t;
  } else if (t < c.landT2) {
    const tb = t - c.landT;
    x = c.landX + c.bounceVx * tb;
    y = c.groundY + c.bounceVy * tb + 0.5 * CASING_G * tb * tb;
    rot = c.rot0 + c.spin * (c.landT + tb * 0.4);
  } else {
    x = c.restX; y = c.groundY; rot = c.restRot;
  }
  ctx.save();
  ctx.globalAlpha = t > c.life - CASING_FADE_S ? Math.max(0, (c.life - t) / CASING_FADE_S) : 1;
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(c.flip * cs, cs);
  drawSprite(ctx, casing, -(casing.bx + casing.bw / 2), -(casing.by + casing.bh / 2));
  ctx.restore();
};

export const spawnMuzzlePuff = (sc: Scene, x: number, y: number, angle: number, oy: number, time: number) => {
  const r = Math.random;
  sc.muzzlePuffs.push({
    x: x + Math.cos(angle) * 10,
    y: y + Math.sin(angle) * 10,
    vx: Math.cos(angle) * (18 + r() * 16),
    vy: Math.sin(angle) * 18 - (30 + r() * 20),
    oy,
    t0: time, life: 0.75 + r() * 0.3,
  });
};

export const drawOneMuzzlePuff = (sc: Scene, p: Scene['muzzlePuffs'][number], time: number) => {
  const { ctx } = sc;
  const t = time - p.t0;
  if (t < 0) return;
  const k = t / p.life;
  if (k >= 1) return;
  const cx = p.x + p.vx * t;
  const cy = p.y + p.vy * t;
  const rad = 9 + k * 24;
  const alpha = (1 - k) * 0.6;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
  g.addColorStop(0, `rgba(226,226,230,${alpha})`);
  g.addColorStop(0.55, `rgba(220,220,226,${alpha * 0.55})`);
  g.addColorStop(1, 'rgba(216,216,224,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fill();
};

const SMOKE_FADE_S = 0.45;
const SMOKE_BLOOM_S = 0.35;
export const drawSmokeScreen = (
  sc: Scene, id: string, s: SmokeScreenFx,
  time: number, state: PlaybackState,
) => {
  const { ctx, smokeCloud } = sc;
  if (s.bornAt == null || time < s.bornAt) return;
  if (sc.byId[id]?.dead && s.fadeAt == null) s.fadeAt = time;
  const bloom = Math.min(1, (time - s.bornAt) / SMOKE_BLOOM_S);
  let alpha = 0.85 * bloom;
  if (s.fadeAt != null) {
    const k = (time - s.fadeAt) / SMOKE_FADE_S;
    if (k >= 1) { state.smokeScreens.delete(id); return; }
    alpha *= 1 - Math.max(0, k);
  }
  const sway = Math.sin(time * 1.6 + s.x) * 16;
  const bob = Math.sin(time * 1.05 + s.x) * 6;
  ctx.save();
  ctx.globalAlpha = alpha;
  const cs = (240 * (0.55 + 0.45 * bloom)) / smokeCloud.bw;
  ctx.translate(s.x + sway, s.y - 90 + bob);
  ctx.scale(cs, cs);
  drawSprite(ctx, smokeCloud, -(smokeCloud.bx + smokeCloud.bw / 2), -(smokeCloud.by + smokeCloud.bh / 2));
  ctx.restore();
};

export const drawFlashbangs = (sc: Scene, time: number) => {
  const { ctx, flashbangs } = sc;
  for (const fb of flashbangs) {
    const k = (time - fb.t0) / fb.life;
    if (k < 0 || k > 1) continue;
    const a = 1 - k;
    ctx.save();
    ctx.globalAlpha = a * 0.8;
    ctx.lineWidth = 6 * (1 - k);
    ctx.strokeStyle = '#fffbe0';
    ctx.beginPath();
    ctx.arc(fb.x, fb.y, 30 + k * 220, 0, Math.PI * 2);
    ctx.stroke();
    const core = 90 * (1 - k * 0.7);
    const g = ctx.createRadialGradient(fb.x, fb.y, 0, fb.x, fb.y, core);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(0.6, `rgba(255,250,210,${a * 0.7})`);
    g.addColorStop(1, 'rgba(255,250,210,0)');
    ctx.globalAlpha = 1;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(fb.x, fb.y, core, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};

export const flashbangWash = (sc: Scene, time: number): number => {
  let wash = 0;
  for (const fb of sc.flashbangs) {
    const k = (time - fb.t0) / fb.life;
    if (k < 0 || k > 0.34) continue;
    wash = Math.max(wash, 0.8 * (1 - k / 0.34));
  }
  return wash;
};

export const drawDamages = (sc: Scene, time: number) => {
  const { ctx, damages } = sc;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const d of damages) {
    const k = (time - d.t0) / d.life;
    if (k < 0 || k > 1) continue;
    const alpha = 1 - k * k;
    const size = d.label ? 22 : d.crit ? 38 : 28;
    const y = d.y - 210 - 80 * k;
    ctx.font = `900 ${size}px system-ui, sans-serif`;
    ctx.lineWidth = 5;
    ctx.strokeStyle = `rgba(0,0,0,${0.55 * alpha})`;
    ctx.fillStyle = d.label
      ? `rgba(215,221,230,${alpha})`
      : d.heal
      ? `rgba(57,208,90,${alpha})`
      : d.absorb ? `rgba(247,185,62,${alpha})`
      : d.shield ? `rgba(90,160,255,${alpha})`
      : d.crit ? `rgba(255,196,64,${alpha})` : `rgba(255,92,80,${alpha})`;
    const text = d.label ?? (d.heal || d.gain ? `+${d.amount}` : `-${d.amount}`);
    ctx.strokeText(text, d.x, y);
    ctx.fillText(text, d.x, y);
  }
  ctx.restore();
};
